import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

interface SessionUser {
  id: string
  email: string
}

interface AppAuth {
  isLoaded: boolean
  isSignedIn: boolean
  userId: string | null
  userEmail: string | null
  hasPortalAccess: boolean
  localDevelopmentBypass: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const localDevelopmentBypass = import.meta.env.VITE_LOCAL_DEV_BYPASS_AUTH === '1'
const AuthContext = createContext<AppAuth | null>(null)

function readCsrfCookie(): string | null {
  const prefix = 'almaz_csrf='
  const value = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix))
  return value ? decodeURIComponent(value.slice(prefix.length)) : null
}

async function readSession(): Promise<SessionUser | null> {
  const response = await fetch('/api/auth/session', { credentials: 'same-origin' })
  if (response.status === 401) return null
  if (!response.ok) throw new Error('Session request failed')
  const payload = await response.json() as { user?: { id?: unknown; email?: unknown } }
  return typeof payload.user?.id === 'string' && typeof payload.user.email === 'string'
    ? { id: payload.user.id, email: payload.user.email }
    : null
}

function LocalAuthBridge({ children }: { children: ReactNode }) {
  const value = useMemo<AppAuth>(() => ({
    isLoaded: true,
    isSignedIn: true,
    userId: null,
    userEmail: null,
    hasPortalAccess: true,
    localDevelopmentBypass: true,
    signIn: async () => undefined,
    signOut: async () => undefined,
  }), [])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function FirstPartyAuthBridge({ children }: { children: ReactNode }) {
  const [loaded, setLoaded] = useState(false)
  const [user, setUser] = useState<SessionUser | null>(null)

  useEffect(() => {
    let active = true
    void readSession().then((next) => {
      if (active) setUser(next)
    }).catch(() => {
      if (active) setUser(null)
    }).finally(() => {
      if (active) setLoaded(true)
    })
    return () => { active = false }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => null) as { detail?: unknown } | null
      throw new Error(typeof payload?.detail === 'string' ? payload.detail : 'Не удалось войти.')
    }
    const next = await response.json() as { user?: { id?: unknown; email?: unknown } }
    if (typeof next.user?.id !== 'string' || typeof next.user.email !== 'string') {
      throw new Error('Сервер вернул некорректную сессию.')
    }
    setUser({ id: next.user.id, email: next.user.email })
  }, [])

  const signOut = useCallback(async () => {
    const csrf = readCsrfCookie()
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
    })
    setUser(null)
  }, [])

  const value = useMemo<AppAuth>(() => ({
    isLoaded: loaded,
    isSignedIn: user !== null,
    userId: user?.id ?? null,
    userEmail: user?.email ?? null,
    hasPortalAccess: true,
    localDevelopmentBypass: false,
    signIn,
    signOut,
  }), [loaded, signIn, signOut, user])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return localDevelopmentBypass
    ? <LocalAuthBridge>{children}</LocalAuthBridge>
    : <FirstPartyAuthBridge>{children}</FirstPartyAuthBridge>
}

export function useAppAuth(): AppAuth {
  const auth = useContext(AuthContext)
  if (!auth) throw new Error('useAppAuth must be used inside AuthProvider')
  return auth
}

export function UserControl({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const { localDevelopmentBypass, signOut, userEmail } = useAppAuth()
  const [open, setOpen] = useState(false)
  if (localDevelopmentBypass) {
    return <span aria-label="Локальный режим" className="grid size-7 place-items-center rounded-full bg-onyx text-[11px] font-semibold text-ivory">Л</span>
  }
  const initial = userEmail?.slice(0, 1).toUpperCase() ?? 'А'
  return <div className="relative">
    <button type="button" aria-label="Учётная запись" onClick={() => setOpen((value) => !value)} className={`grid ${size === 'sm' ? 'size-7' : 'size-8'} place-items-center rounded-full bg-onyx text-[11px] font-semibold text-ivory`}>
      {initial}
    </button>
    {open && <div className="absolute right-0 top-[calc(100%+8px)] z-[90] w-64 rounded-2xl border border-rule bg-paper p-2 shadow-xl">
      <div className="truncate px-3 py-2 text-[12px] text-ink-muted">{userEmail}</div>
      <button type="button" onClick={() => void signOut()} className="w-full rounded-xl px-3 py-2 text-left text-[14px] text-ink hover:bg-bone">Выйти</button>
    </div>}
  </div>
}

export function SignInControl({ children }: { children: ReactNode }) {
  return <span role="link" tabIndex={0} onClick={() => { window.location.href = '/sign-in' }} onKeyDown={(event) => { if (event.key === 'Enter') window.location.href = '/sign-in' }}>{children}</span>
}
